import { useInterviewSession } from '@/hooks/useInterviewSession'
import type { ChatMessage, RecordingData } from '@bosmart/types'
import { EndInterviewDialog } from './interview-session/EndInterviewDialog'
import { SessionControls } from './interview-session/SessionControls'
import { SessionHeader } from './interview-session/SessionHeader'
import { SessionStyles } from './interview-session/SessionStyles'
import { ThankYouScreen } from './interview-session/ThankYouScreen'
import { TranscriptPanel } from './interview-session/TranscriptPanel'
import { VideoGrid } from './interview-session/VideoGrid'

export interface InterviewSessionProps {
  interviewId: string
  token?: string
  otpProofToken?: string
  onEnd: (history: ChatMessage[], recording?: RecordingData) => void
}

const InterviewSession = ({ interviewId, token, otpProofToken, onEnd }: InterviewSessionProps) => {
  const { refs, state, actions } = useInterviewSession({
    interviewId,
    token,
    otpProofToken,
    onEnd,
  })

  return (
    <>
      <SessionStyles />
      <div className="relative w-full h-full flex flex-col bg-dark-900 overflow-hidden">
        {state.showThankYou && <ThankYouScreen />}
        {state.showConfirmation && !state.showThankYou && (
          <EndInterviewDialog
            onCancel={actions.cancelEndSession}
            onConfirm={() => {
              void actions.confirmEndSession()
            }}
          />
        )}
        {state.showStartCue && !state.showThankYou && (
          <div className="absolute inset-0 border-4 border-brand-500/50 pointer-events-none z-50 animate-pulse-fast transition-opacity duration-1000" />
        )}

        <SessionHeader
          isLive={state.hasLiveService}
          aiSpeaking={state.aiSpeaking}
          isTimerRunning={state.isTimerRunning}
          formattedDuration={state.formattedDuration}
        />

        <VideoGrid
          videoRef={refs.videoRef}
          pipVideoRef={refs.pipVideoRef}
          canvasRef={refs.canvasRef}
          isScreenSharing={state.isScreenSharing}
          showStartCue={state.showStartCue}
          showSpeakFirstPrompt={state.showSpeakFirstPrompt}
          showThankYou={state.showThankYou}
          notification={state.notification}
          error={state.error}
          hasLiveService={state.hasLiveService}
          status={state.status}
          onStart={() => {
            void actions.startInterview()
          }}
          onReload={actions.reload}
        />

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/90 to-transparent pointer-events-none z-10" />
        <div className="absolute bottom-0 left-0 right-0 z-20 pt-16 pb-8 px-8 pointer-events-none">
          <div className="max-w-4xl mx-auto pointer-events-auto">
            <TranscriptPanel
              transcription={state.transcription}
              modelActive={state.modelActive}
              userActive={state.userActive}
            />
            <SessionControls
              isMuted={state.isMuted}
              isScreenSharing={state.isScreenSharing}
              isCallEnding={state.isCallEnding}
              showConfirmation={state.showConfirmation}
              status={state.status}
              onToggleMute={actions.toggleMute}
              onToggleScreenShare={() => {
                void actions.toggleScreenShare()
              }}
              onEndCall={actions.requestEndSession}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default InterviewSession
